import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, StateCard, fmtMoney } from './AcademyShared';

export default function AcademyOverview() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/companies/${companyId}/academy/overview`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed');
      setData(await res.json());
    } catch (e) { setError('Unable to load the academy overview. Please try again.'); }
    setLoading(false);
  }, [companyId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (error) return <div role="alert" style={{ padding: 32 }}>{error} <button onClick={fetchData}>Retry</button></div>;
  if (initialLoading || !data) return <div style={{ padding: 32, textAlign: 'center' }}>Loading...</div>;

  const summary = data.summary_cards || {};
  const overdue = (data.pending_actions || []).find(action => action.type === 'overdue_student_payment')?.count || 0;

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>🎓 Academy Management Overview</h2>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 14 }}>
        <StateCard label="Active Cohorts" count={summary.active_cohorts} color="#3b82f6" icon="📚" />
        <StateCard label="Active Students" count={summary.unique_active_students} color="#8b5cf6" icon="🧑‍🎓" />
        <StateCard label="Total Withdrawn Students" count={summary.total_withdrawn_students} color="#ef4444" icon="↩️" />
        <StateCard label="Active Enrollments" count={summary.active_enrollments} color="#10b981" icon="📝" />
        <StateCard label="Certificates Issued" count={data.certificate_summary?.certificates_issued} color="#f59e0b" icon="🏆" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 14 }}>
        {[
          ['Total Overdue Amount', summary.total_overdue_amount, '#ef4444'],
          ['Total Discounts', summary.total_discounts, '#8b5cf6'],
        ].map(([label, amount, color]) => (
          <div key={label} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 18px', borderLeft: `4px solid ${color}` }}>
            <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, marginBottom: 6 }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>{fmtMoney(amount)}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 18px', borderLeft: '4px solid #10b981' }}>
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, marginBottom: 6 }}>Total Collected</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>{fmtMoney(summary.total_collected)}</div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 18px', borderLeft: '4px solid #f59e0b' }}>
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, marginBottom: 6 }}>Total Pending</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>{fmtMoney(summary.total_outstanding)}</div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 18px', borderLeft: '4px solid #10b981' }}>
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, marginBottom: 6 }}>Total Income</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>{fmtMoney(summary.academy_income)}</div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 18px', borderLeft: '4px solid #ef4444' }}>
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, marginBottom: 6 }}>Total Expenses</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>{fmtMoney(summary.total_expenses)}</div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 18px', borderLeft: '4px solid #3b82f6' }}>
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, marginBottom: 6 }}>Net</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>{fmtMoney((summary.academy_income || 0) - (summary.total_expenses || 0))}</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14, marginTop: 14 }}>
        <StateCard label="Overdue Installments" count={overdue} color="#ef4444" icon="⚠️" />
        <StateCard label="Cohorts Pending Settlement" count={summary.finished_pending_settlement_cohorts} color="#f59e0b" icon="⏳" />
      </div>
    </div>
  );
}
